import React from 'react';
import type { Rule, RuleCondition, RuleAction, RuleBuilderDialogProps, TimesheetField, RuleExecutionTiming } from './types';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { TIMESHEET_FIELDS, RULE_ACTIONS } from './constants';
import { generateRuleId } from './utils';
import { generatePlaceholderStageId } from './placeholderStageUtils';
import { Info, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export const RuleDrawer: React.FC<RuleBuilderDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  existingRule,
  executionTiming: initialExecutionTiming,
  context: initialContext,
  availableStages,
  workflowContext,
}) => {
  const [ruleName, setRuleName] = React.useState('');
  const [executionTiming, setExecutionTiming] = React.useState<RuleExecutionTiming>(
    initialExecutionTiming || 'pre-approval'
  );
  const [context, setContext] = React.useState<'approved' | 'rejected' | undefined>(initialContext);
  const [condition, setCondition] = React.useState<RuleCondition>({
    field: 'TST_TOTAL_HOURS',
    operator: '>',
    value: '',
  });
  const [ifTrue, setIfTrue] = React.useState<RuleAction>({
    action: executionTiming === 'pre-approval' ? 'skip' : 'continue',
    message: '',
    evaluateTargetStageRules: false,
  });
  const [ifFalse, setIfFalse] = React.useState<RuleAction>({
    action: 'continue',
    message: '',
    evaluateTargetStageRules: false,
  });

  React.useEffect(() => {
    if (existingRule) {
      setRuleName(existingRule.name);
      setExecutionTiming(existingRule.executionTiming);
      setContext(existingRule.context);
      setCondition(existingRule.condition);
      setIfTrue(existingRule.ifTrue);
      setIfFalse(existingRule.ifFalse);
    } else {
      // Reset form
      setRuleName('');
      setExecutionTiming(initialExecutionTiming || 'pre-approval');
      setContext(initialContext);
      setCondition({
        field: 'TST_TOTAL_HOURS',
        operator: '>',
        value: '',
      });
      setIfTrue({
        action: (initialExecutionTiming || 'pre-approval') === 'pre-approval' ? 'skip' : 'continue',
        message: '',
        evaluateTargetStageRules: false,
      });
      setIfFalse({
        action: 'continue',
        message: '',
        evaluateTargetStageRules: false,
      });
    }
  }, [existingRule, isOpen, initialExecutionTiming, initialContext]);

  const getSelectedField = (): TimesheetField | undefined => {
    return TIMESHEET_FIELDS.find((f) => f.id === condition.field);
  };

  const getAvailableOperators = () => {
    const field = getSelectedField();
    return field?.operators || [];
  };

  const handleFieldChange = (fieldId: string) => {
    const field = TIMESHEET_FIELDS.find((f) => f.id === fieldId);
    setCondition({
      ...condition,
      field: fieldId,
      operator: field?.operators[0]?.id || '=',
      value: '',
    });
  };

  const handleOperatorChange = (operator: string) => {
    setCondition({
      ...condition,
      operator,
    });
  };

  const handleValueChange = (value: string) => {
    const field = getSelectedField();
    if (field?.type === 'number') {
      setCondition({
        ...condition,
        value: value === '' ? '' : parseFloat(value) || 0,
      });
    } else {
      setCondition({
        ...condition,
        value,
      });
    }
  };

  const handleIfTrueActionChange = (action: string) => {
    setIfTrue({
      ...ifTrue,
      action: action as any,
      targetStageId: undefined,
      targetStageName: undefined,
    });
  };

  const handleIfTrueStageChange = (stageId: string) => {
    if (stageId === '__create_new__') {
      setIfTrue({
        ...ifTrue,
        targetStageId: '__create_new__',
        targetStageName: '',
      });
    } else {
      const stage = availableStages.find((s) => s.id === stageId);
      setIfTrue({
        ...ifTrue,
        targetStageId: stageId,
        targetStageName: stage?.name,
      });
    }
  };

  const handleIfFalseActionChange = (action: string) => {
    setIfFalse({
      ...ifFalse,
      action: action as any,
      targetStageId: undefined,
      targetStageName: undefined,
    });
  };

  const handleIfFalseStageChange = (stageId: string) => {
    if (stageId === '__create_new__') {
      setIfFalse({
        ...ifFalse,
        targetStageId: '__create_new__',
        targetStageName: '',
      });
    } else {
      const stage = availableStages.find((s) => s.id === stageId);
      setIfFalse({
        ...ifFalse,
        targetStageId: stageId,
        targetStageName: stage?.name,
      });
    }
  };

  const handleSave = () => {
    // Process ifTrue action - generate placeholder ID if needed
    const processedIfTrue = { ...ifTrue };
    if (ifTrue.action === 'gotoStage' && ifTrue.targetStageId === '__create_new__' && ifTrue.targetStageName) {
      processedIfTrue.targetStageId = generatePlaceholderStageId(ifTrue.targetStageName);
    }

    // Process ifFalse action - generate placeholder ID if needed
    const processedIfFalse = { ...ifFalse };
    if (ifFalse.action === 'gotoStage' && ifFalse.targetStageId === '__create_new__' && ifFalse.targetStageName) {
      processedIfFalse.targetStageId = generatePlaceholderStageId(ifFalse.targetStageName);
    }

    const rule: Rule = {
      id: existingRule?.id || generateRuleId(),
      name: ruleName,
      order: existingRule?.order || 1,
      executionTiming,
      context: executionTiming === 'post-approval' ? context : undefined,
      condition,
      ifTrue: processedIfTrue,
      ifFalse: processedIfFalse,
    };
    onSave(rule);
    onClose();
  };

  const isValid = () => {
    const field = getSelectedField();
    const isUnaryOperator = field?.type === 'boolean'; // isTrue/isFalse don't need values

    const baseValid = (
      ruleName.trim().length > 0 &&
      condition.field &&
      condition.operator &&
      (isUnaryOperator || condition.value !== '')
    );

    if (!baseValid) return false;

    // Validate ifTrue gotoStage
    if (ifTrue.action === 'gotoStage') {
      if (!ifTrue.targetStageId) return false;
      if (ifTrue.targetStageId === '__create_new__' && !ifTrue.targetStageName?.trim()) return false;
    }

    // Validate ifFalse gotoStage
    if (ifFalse.action === 'gotoStage') {
      if (!ifFalse.targetStageId) return false;
      if (ifFalse.targetStageId === '__create_new__' && !ifFalse.targetStageName?.trim()) return false;
    }

    return true;
  };

  const availableActions = RULE_ACTIONS[executionTiming === 'pre-approval' ? 'preStage' : 'postStage'];

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-[600px] overflow-y-auto"
      >
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle>
            {existingRule ? 'Edit Rule' : 'Add Rule'}
          </SheetTitle>
          <SheetDescription>
            {existingRule 
              ? 'Update the rule configuration below' 
              : 'Configure a smart rule to automate workflow decisions'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 pb-6">
          {/* Rule Name */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-3">Rule Details</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ruleName">
                    Rule Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="ruleName"
                    placeholder="e.g., Skip for low hours"
                    value={ruleName}
                    onChange={(e) => setRuleName(e.target.value)}
                  />
                </div>

                {/* Execution Timing Display */}
                <div className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  executionTiming === 'pre-approval' 
                    ? 'bg-blue-500/5 border-blue-500/30' 
                    : 'bg-green-500/5 border-green-500/30'
                )}>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">
                      {executionTiming === 'pre-approval' ? '🔵' : '🟢'}
                    </span>
                    <div>
                      <p className="text-sm font-semibold">
                        {executionTiming === 'pre-approval' 
                          ? 'Before Stage' 
                          : 'After Decision'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {executionTiming === 'pre-approval' 
                          ? 'Validates before stage begins'
                          : 'Routes after approve/reject'}
                      </p>
                    </div>
                  </div>
                  
                  {executionTiming === 'post-approval' && (
                    <div className="flex items-center space-x-2">
                      <Label className="text-xs text-muted-foreground">When:</Label>
                      <fieldset disabled={!!existingRule}>
                        <RadioGroup
                          value={context || 'approved'}
                          onValueChange={(value) => setContext(value as 'approved' | 'rejected')}
                          className="flex items-center space-x-3"
                        >
                          <div className="flex items-center space-x-1">
                            <RadioGroupItem value="approved" id="context-approved" />
                            <Label htmlFor="context-approved" className="cursor-pointer text-xs">
                              Approved
                            </Label>
                          </div>
                          <div className="flex items-center space-x-1">
                            <RadioGroupItem value="rejected" id="context-rejected" />
                            <Label htmlFor="context-rejected" className="cursor-pointer text-xs">
                              Rejected
                            </Label>
                          </div>
                        </RadioGroup>
                      </fieldset>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Condition Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-3">Condition</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Define when this rule should trigger
                </p>
              </div>

              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-muted-foreground">If</span>
                  <Select value={condition.field} onValueChange={handleFieldChange}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMESHEET_FIELDS.map((field) => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={condition.operator} onValueChange={handleOperatorChange}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableOperators().map((op) => (
                        <SelectItem key={op.id} value={op.id}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {getSelectedField()?.type !== 'boolean' && (
                    <Input
                      type={getSelectedField()?.type === 'number' ? 'number' : 'text'}
                      placeholder="Value"
                      value={condition.value}
                      onChange={(e) => handleValueChange(e.target.value)}
                      className="w-[120px]"
                    />
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* When Condition Matches Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <h3 className="text-sm font-medium">When Condition Matches</h3>
              </div>

              <div className="space-y-4 p-4 border rounded-lg bg-green-500/5 border-green-500/30">
                <div className="space-y-2">
                  <Label htmlFor="ifTrueAction">Action</Label>
                  <Select value={ifTrue.action} onValueChange={handleIfTrueActionChange}>
                    <SelectTrigger id="ifTrueAction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableActions.map((action) => (
                        <SelectItem key={action.id} value={action.id}>
                          {action.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {ifTrue.action === 'gotoStage' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="ifTrueStage">Target Stage</Label>
                      <Select value={ifTrue.targetStageId} onValueChange={handleIfTrueStageChange}>
                        <SelectTrigger id="ifTrueStage">
                          <SelectValue placeholder="Select or create stage..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableStages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="__create_new__" className="text-primary font-medium border-t mt-1 pt-1">
                            + Create New Stage Reference
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {ifTrue.targetStageId === '__create_new__' && (
                      <div className="space-y-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <Label htmlFor="ifTrueNewStageName" className="text-sm">
                          New Stage Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="ifTrueNewStageName"
                          placeholder="e.g., Finance Review"
                          value={ifTrue.targetStageName || ''}
                          onChange={(e) => setIfTrue({ ...ifTrue, targetStageName: e.target.value })}
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>This creates a placeholder. You'll need to configure this stage before activating the workflow.</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="ifTrueEvaluate"
                        checked={ifTrue.evaluateTargetStageRules}
                        onCheckedChange={(checked) =>
                          setIfTrue({ ...ifTrue, evaluateTargetStageRules: checked as boolean })
                        }
                      />
                      <Label htmlFor="ifTrueEvaluate" className="text-sm cursor-pointer">
                        Evaluate target stage rules
                      </Label>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="ifTrueMessage">
                      User Message (Optional)
                    </Label>
                    <div className="group relative">
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-popover text-popover-foreground text-xs rounded-md shadow-lg border border-border z-50">
                        If provided, this message will be shown to the user with Accept/Decline options. If declined, the workflow will be aborted.
                      </div>
                    </div>
                  </div>
                  <Input
                    id="ifTrueMessage"
                    placeholder="e.g., Hours below threshold, skipping approval"
                    value={ifTrue.message}
                    onChange={(e) => setIfTrue({ ...ifTrue, message: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* When Condition Does Not Match Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">When Condition Does Not Match</h3>
              </div>

              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="ifFalseAction">Action</Label>
                  <Select value={ifFalse.action} onValueChange={handleIfFalseActionChange}>
                    <SelectTrigger id="ifFalseAction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableActions.map((action) => (
                        <SelectItem key={action.id} value={action.id}>
                          {action.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {ifFalse.action === 'gotoStage' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="ifFalseStage">Target Stage</Label>
                      <Select value={ifFalse.targetStageId} onValueChange={handleIfFalseStageChange}>
                        <SelectTrigger id="ifFalseStage">
                          <SelectValue placeholder="Select or create stage..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableStages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="__create_new__" className="text-primary font-medium border-t mt-1 pt-1">
                            + Create New Stage Reference
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {ifFalse.targetStageId === '__create_new__' && (
                      <div className="space-y-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <Label htmlFor="ifFalseNewStageName" className="text-sm">
                          New Stage Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="ifFalseNewStageName"
                          placeholder="e.g., Finance Review"
                          value={ifFalse.targetStageName || ''}
                          onChange={(e) => setIfFalse({ ...ifFalse, targetStageName: e.target.value })}
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>This creates a placeholder. You'll need to configure this stage before activating the workflow.</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="ifFalseEvaluate"
                        checked={ifFalse.evaluateTargetStageRules}
                        onCheckedChange={(checked) =>
                          setIfFalse({ ...ifFalse, evaluateTargetStageRules: checked as boolean })
                        }
                      />
                      <Label htmlFor="ifFalseEvaluate" className="text-sm cursor-pointer">
                        Evaluate target stage rules
                      </Label>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="ifFalseMessage">
                      User Message (Optional)
                    </Label>
                    <div className="group relative">
                      <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-popover text-popover-foreground text-xs rounded-md shadow-lg border border-border z-50">
                        If provided, this message will be shown to the user with Accept/Decline options. If declined, the workflow will be aborted.
                      </div>
                    </div>
                  </div>
                  <Input
                    id="ifFalseMessage"
                    placeholder="e.g., Standard approval required"
                    value={ifFalse.message}
                    onChange={(e) => setIfFalse({ ...ifFalse, message: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid()}>
            {existingRule ? 'Update Rule' : 'Save Rule'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
